import { AnimatePresence, motion } from 'framer-motion';
import { LuBookOpen, LuChevronRight, LuCompass } from 'react-icons/lu';
import { countOf } from '../../utils/plural';
import { SidebarDomainItem } from './SidebarDomainItem';
import type { DomainWithPaths } from './types';

interface CourseSidebarProps {
  domains: DomainWithPaths[];
  isCollapsed: boolean;
  onToggle: () => void;
  expandedDomains: Record<number, boolean>;
  currentPathId: number | null;
  onDomainToggle: (domainId: number) => void;
  onPathClick: (domainId: number, pathId: number) => void;
}

/**
 * Desktop "Course Navigation" sidebar (redesign PR4, course.css): a white
 * surface under the top bar with the unit rows, the collapse control in its
 * heading row and the units / chapters footer. Same behaviour as before.
 */
export function CourseSidebar({
  domains,
  isCollapsed,
  onToggle,
  expandedDomains,
  currentPathId,
  onDomainToggle,
  onPathClick,
}: CourseSidebarProps) {
  return (
    <>
      {/* Toggle Button - Always visible */}
      <motion.button
        type="button"
        onClick={onToggle}
        initial={false}
        animate={{
          left: isCollapsed ? 8 : 236,
        }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="ee-course-side__toggle"
        title={isCollapsed ? 'Open sidebar' : 'Close sidebar'}
      >
        <motion.span
          animate={{ rotate: isCollapsed ? 0 : 180 }}
          transition={{ duration: 0.2 }}
          className="ee-course-side__toggle-icon"
        >
          <LuChevronRight aria-hidden="true" />
        </motion.span>
      </motion.button>

      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{
          width: isCollapsed ? 0 : 280,
          opacity: isCollapsed ? 0 : 1,
        }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="ee-course-side"
      >
        <AnimatePresence>
          {!isCollapsed && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2, delay: 0.1 }}
              className="ee-course-side__inner"
            >
              {/* Header */}
              <div className="ee-course-side__head">
                <h2 className="ee-course-side__title">
                  <LuCompass aria-hidden="true" />
                  Course Navigation
                </h2>
              </div>

              {/* Domains List */}
              <div className="ee-course-side__list">
                {domains.map((domain) => (
                  <SidebarDomainItem
                    key={domain.id}
                    domain={domain}
                    isExpanded={expandedDomains[domain.id] || false}
                    currentPathId={currentPathId}
                    onToggle={() => onDomainToggle(domain.id)}
                    onPathClick={(pathId) => onPathClick(domain.id, pathId)}
                  />
                ))}

                {domains.length === 0 && (
                  <div className="ee-course-side__empty">
                    <LuBookOpen aria-hidden="true" />
                    No courses available
                  </div>
                )}
              </div>

              {/* Footer Stats */}
              <div className="ee-course-side__foot">
                <span>{countOf(domains.length, 'unit')}</span>
                <span>
                  {countOf(
                    domains.reduce((acc, d) => acc + (d.paths?.length || 0), 0),
                    'chapter',
                  )}
                </span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.aside>
    </>
  );
}
